// Dragging a column OUT of the object tree.
//
// The tree on the left already lists every column of every form; a designer
// picks one there to edit it. So a layout that is authored by choosing columns
// — the Feed view — drags from that same list rather than printing a second
// copy of it beside itself. The payload rides the drag event itself, which is
// what lets the two panels talk without a store between them.

export const COLUMN_MIME = 'application/x-twasta-column'

export interface ColumnDragPayload {
  /** Index into `model.sql_models` — the form the column belongs to. */
  formIndex: number
  /** The column's db_name: the name every layout stores a reference by. */
  name: string
  /** What the tree showed, for a message the designer recognises. */
  label?: string
}

// What is in flight, held here as well as on the event. A custom MIME type is
// the correct channel but not a reliable one — several browsers hand back an
// empty `types` list for an unknown flavour during dragover, and the drop
// target then never calls preventDefault, which reads as "drag and drop simply
// doesn't work". The transaction designer's own field drag has always kept a
// ref for the same reason; this is that, shared.
let inFlight: ColumnDragPayload | null = null

export function setColumnDrag(e: React.DragEvent, payload: ColumnDragPayload): void {
  inFlight = payload
  try {
    e.dataTransfer.effectAllowed = 'copyMove'
    e.dataTransfer.setData(COLUMN_MIME, JSON.stringify(payload))
    // A plain-text twin so the drag is legible to anything that isn't us
    // (and to browsers that insist on a text/plain flavour being present).
    e.dataTransfer.setData('text/plain', payload.name)
  } catch {
    // No transfer object to write to — the ref above still carries the drag.
  }
}

/** Call from onDragEnd, so a drag that was abandoned outside a well doesn't
 *  leave a stale payload behind. */
export function clearColumnDrag(): void {
  inFlight = null
}

/** True while a column drag is over this element. Only the TYPES are readable
 *  during dragover — the value arrives on drop. */
export function hasColumnDrag(e: React.DragEvent): boolean {
  try {
    if (Array.from(e.dataTransfer.types || []).includes(COLUMN_MIME)) return true
  } catch {
    // fall through to the ref
  }
  return inFlight != null
}

export function readColumnDrag(e: React.DragEvent): ColumnDragPayload | null {
  try {
    const raw = e.dataTransfer.getData(COLUMN_MIME)
    if (raw) {
      const parsed = JSON.parse(raw) as ColumnDragPayload
      if (parsed && typeof parsed.name === 'string' && parsed.name) return parsed
    }
  } catch {
    // fall through to the ref
  }
  return inFlight
}
